import React, { isValidElement, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Container,
  Stack,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { approveFundRequest, getFundRequest, rejectFundRequest, releaseFundRequest, uploadFundAttachment, listInstallationTeams, remindFundRequest } from '../../services/fundRequestService';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import PaymentsIcon from '@mui/icons-material/Payments';
import DownloadIcon from '@mui/icons-material/Download';
import { useAuth } from '../../context/AuthContext';

const TYPE_LABELS = {
  GENERAL: 'PENGAJUAN DANA',
  CASH_ADVANCE: 'KASBON',
  TRAVEL: 'PERJALANAN / TIKET'
};

const StatusChip = ({ status }) => {
  const map = {
    draft: { label: 'DRAF', color: 'default' },
    submitted: { label: 'DIAJUKAN', color: 'info' },
    in_review: { label: 'SEDANG DITINJAU', color: 'warning' },
    approved: { label: 'DISETUJUI', color: 'info' },
    rejected: { label: 'DITOLAK', color: 'error' },
    released: { label: 'DICAIRKAN', color: 'success' }
  };
  const cfg = map[status] || { label: (status || '').toUpperCase(), color: 'default' };
  return <Chip size="small" color={cfg.color} label={cfg.label} />;
};

const buildFileUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
  const backendBase = apiBase.replace(/\/api$/, '');
  if (url.startsWith('/api/')) return `${backendBase}${url}`;
  return `${backendBase}/api/uploads/${url.replace(/^\/+/, '')}`;
};

const formatFileSize = (size) => {
  if (!size && size !== 0) return '';
  if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(2)} MB`;
  if (size >= 1024) return `${(size / 1024).toFixed(2)} KB`;
  return `${size} B`;
};

const normalizeAttachment = (attachment, index = 0) => {
  if (!attachment) return null;
  const rawUrl = typeof attachment === 'string'
    ? attachment
    : attachment.fileUrl || attachment.url || attachment.path || '';
  const fullUrl = buildFileUrl(rawUrl);
  if (!rawUrl || !fullUrl) return null;

  const rawName = attachment.fileName
    || attachment.filename
    || (rawUrl.split('?')[0].split('/').pop() || `lampiran-${index + 1}`);
  const fileName = decodeURIComponent(rawName);
  const fileType = (attachment.fileType || '').toLowerCase();
  const extension = fileType.includes('/')
    ? fileType.split('/').pop()
    : (fileName.split('.').pop() || '').toLowerCase();
  const imageExt = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];

  return {
    id: attachment.id || `${rawUrl}-${index}`,
    fileName,
    url: fullUrl,
    isImage: imageExt.includes(extension),
    isPdf: extension === 'pdf',
    fileType: attachment.fileType || attachment.mimetype || '',
    fileSize: attachment.fileSize || attachment.size || null
  };
};

const formatDateLong = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('id-ID', { day: '2-digit', month: 'long', year: 'numeric' }).format(date);
};

const toJakartaDateFromParts = (date, time) => {
  if (!date) return null;
  const [y, m, d] = String(date).split('-').map(Number);
  if (!y || !m || !d) return null;
  const timeMatch = String(time || '').match(/(?<h>\d{1,2})(?::(?<m>\d{1,2}))?/);
  const hours = timeMatch?.groups?.h ? Number(timeMatch.groups.h) : 0;
  const minutes = timeMatch?.groups?.m ? Number(timeMatch.groups.m) : 0;
  const hh = String(Number.isFinite(hours) ? hours : 0).padStart(2, '0');
  const mm = String(Number.isFinite(minutes) ? minutes : 0).padStart(2, '0');
  return new Date(`${date}T${hh}:${mm}:00+07:00`);
};

const formatDateTimeLabel = (date, time) => {
  const dt = toJakartaDateFromParts(date, time) || (date ? new Date(`${date}T00:00:00+07:00`) : null);
  if (!dt || Number.isNaN(dt.getTime())) return '-';
  return new Intl.DateTimeFormat('id-ID', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Jakarta'
  }).format(dt);
};

const formatTimestamp = (value) => {
  if (!value) return '-';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return '-';
  return new Intl.DateTimeFormat('id-ID', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Jakarta'
  }).format(dt);
};

const formatCurrency = (value) => `Rp ${Number(value || 0).toLocaleString('id-ID')}`;

const InfoValue = ({ value }) => {
  if (value === null || value === undefined || value === '') {
    return <Typography variant="body1" fontWeight={600}>-</Typography>;
  }
  if (isValidElement(value)) return value;
  return <Typography variant="body1" fontWeight={600}>{value}</Typography>;
};

const InfoGrid = ({ items = [] }) => (
  <Grid container spacing={2}>
    {items.filter(Boolean).map((item, index) => (
      <Grid key={item?.key || `${item?.label || 'info'}-${index}`} item xs={12} sm={item?.fullWidth ? 12 : 6}>
        <Typography variant="caption" color="text.secondary">
          {item?.label}
        </Typography>
        <Box sx={{ mt: 0.5 }}>
          <InfoValue value={item?.value} />
        </Box>
        {item?.helper ? (
          <Typography variant="caption" color="text.secondary">
            {item.helper}
          </Typography>
        ) : null}
      </Grid>
    ))}
  </Grid>
);

const SectionCard = ({ title, subtitle, actions, children, sx, ...rest }) => (
  <Card
    variant="outlined"
    sx={{
      mb: 2,
      borderRadius: 2,
      borderColor: 'divider',
      boxShadow: '0 1px 2px rgba(16,24,40,0.06)',
      ...sx
    }}
    {...rest}
  >
    <CardHeader
      title={title}
      subheader={subtitle}
      action={actions}
      titleTypographyProps={{ variant: 'subtitle1', fontWeight: 700 }}
      subheaderTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
      sx={{ pb: 1 }}
    />
    <Divider />
    <CardContent sx={{ pt: 2 }}>
      {children}
    </CardContent>
  </Card>
);

const QuickStatCard = ({ label, value, helper }) => (
  <Card
    variant="outlined"
    sx={{
      borderRadius: 2,
      borderColor: 'divider',
      boxShadow: '0 1px 2px rgba(16,24,40,0.04)',
      height: '100%'
    }}
  >
    <CardContent>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {label}
      </Typography>
      {isValidElement(value) ? (
        <Box sx={{ mt: 0.75 }}>
          {value}
        </Box>
      ) : (
        <Typography variant="h6" sx={{ mt: 0.5, fontWeight: 800, lineHeight: 1.2 }}>
          {value}
        </Typography>
      )}
      {helper ? (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
          {helper}
        </Typography>
      ) : null}
    </CardContent>
  </Card>
);

const StepStatusChip = ({ status }) => {
  const map = {
    pending: { label: 'MENUNGGU', color: 'default' },
    approved: { label: 'DISETUJUI', color: 'success' },
    rejected: { label: 'DITOLAK', color: 'error' },
    skipped: { label: 'DILEWATI', color: 'info' }
  };
  const cfg = map[status] || { label: (status || '').toUpperCase(), color: 'default' };
  return <Chip size="small" color={cfg.color} label={cfg.label} />;
};

const ApprovalHistory = ({ steps }) => {
  const filteredSteps = (steps || []).filter(step => {
    const roleName = (step.role?.name || step.roleName || '').toLowerCase();
    return roleName !== 'accounting';
  });
  const sortedSteps = [...filteredSteps].sort((a, b) => a.stepOrder - b.stepOrder);
  if (!sortedSteps.length) {
    return <Typography variant="body2" color="text.secondary">BELUM ADA ALUR PERSETUJUAN</Typography>;
  }
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>LEVEL</TableCell>
            <TableCell>STATUS</TableCell>
            <TableCell>DISETUJUI / DITOLAK OLEH</TableCell>
            <TableCell>WAKTU</TableCell>
            <TableCell>CATATAN</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedSteps.map(step => {
            const approverName = step.actor?.fullName
              || step.actor?.username
              || step.actorName
              || (step.actedBy ? `ID ${step.actedBy}` : '-');
            return (
              <TableRow key={step.id}>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    LEVEL {step.stepOrder}
                  </Typography>
                </TableCell>
                <TableCell>
                  <StepStatusChip status={step.status} />
                </TableCell>
                <TableCell>
                  {approverName}
                </TableCell>
                <TableCell>
                  {step.actedAt ? formatTimestamp(step.actedAt) : '-'}
                </TableCell>
                <TableCell>
                  {step.note ? (
                    <Typography variant="body2">{step.note}</Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">-</Typography>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const FundRequestDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionNote, setActionNote] = useState('');
  const [releaseInfo, setReleaseInfo] = useState({ referenceNo: '', amount: '', description: '' });
  const [releaseFile, setReleaseFile] = useState(null);
  const [dialog, setDialog] = useState(null); // 'approve' | 'reject' | 'release'
  const [mandorLookup, setMandorLookup] = useState({});
  const [remindLoading, setRemindLoading] = useState(false);
  const [remindCooldown, setRemindCooldown] = useState(null);
  const remindCooldownKey = useMemo(() => `fund-req-remind-${id}`, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await getFundRequest(id);
      setData(res.data || res);
    } catch (err) {
      console.error('Failed to fetch detail', err);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, [id]);

  useEffect(() => {
    const raw = localStorage.getItem(remindCooldownKey);
    if (raw) {
      const parsed = Number(raw);
      if (!Number.isNaN(parsed)) setRemindCooldown(parsed);
    }
  }, [remindCooldownKey]);

  const mandorIds = useMemo(() => {
    const ids = [];
    if (data?.cashAdvance?.mandorId) ids.push(data.cashAdvance.mandorId);
    if (data?.travelDetail?.mandorId) ids.push(data.travelDetail.mandorId);
    return Array.from(new Set(ids.filter(Boolean).map(id => String(id))));
  }, [data?.cashAdvance?.mandorId, data?.travelDetail?.mandorId]);

  useEffect(() => {
    if (!mandorIds.length) return;
    const loadMandors = async () => {
      try {
        const teams = await listInstallationTeams('');
        const lookup = {};
        (teams || []).forEach(team => {
          if (!team?.id) return;
          lookup[String(team.id)] = team.name || `Mandor ${team.id}`;
        });
        setMandorLookup(prev => ({ ...prev, ...lookup }));
      } catch (err) {
        console.error('Failed to load mandor list', err);
      }
    };
    loadMandors();
  }, [mandorIds]);

  const pendingStep = useMemo(() => {
    if (!data?.approvalSteps) return null;
    return [...data.approvalSteps]
      .sort((a, b) => a.stepOrder - b.stepOrder)
      .find(s => {
        const roleName = (s.role?.name || '').toLowerCase();
        return s.status === 'pending' && roleName !== 'accounting';
      });
  }, [data]);

  const effectiveStatus = useMemo(() => {
    if (data?.status !== 'in_review') return data?.status;
    const hasPendingNonAccounting = (data?.approvalSteps || []).some(
      s => s.status === 'pending' && (s.role?.name || '').toLowerCase() !== 'accounting'
    );
    return hasPendingNonAccounting ? data.status : 'approved';
  }, [data]);

  const canApprove = pendingStep && user?.role === pendingStep.role?.name;
  // Accounting may release any type; GS (General Support) only TRAVEL requests.
  // Mirrors the type-scoped authorization in backend fundRequests/service.js.
  const canRelease = effectiveStatus === 'approved' && (
    user?.role === 'accounting' ||
    (user?.role === 'gs' && data?.type === 'TRAVEL')
  );
  const resolveMandorName = (manualName, mandorId, fallbackName) => {
    if (manualName) return manualName;
    if (fallbackName) return fallbackName;
    if (mandorId) {
      const idKey = String(mandorId);
      return mandorLookup[idKey] || `ID ${mandorId}`;
    }
    return '-';
  };

  const isRemindAllowedStatus = ['submitted', 'in_review'].includes((data?.status || '').toLowerCase());
  const cooldownMs = remindCooldown ? Math.max(0, 60 * 60 * 1000 - (Date.now() - remindCooldown)) : 0;
  const remindDisabled = !pendingStep || !isRemindAllowedStatus || cooldownMs > 0 || remindLoading;
  const remindHint = cooldownMs > 0
    ? `Tunggu ${Math.ceil(cooldownMs / 6000)} menit untuk kirim pengingat lagi`
    : 'Kirim pengingat ke approver aktif';

  const handleRemind = async () => {
    if (!pendingStep) return;
    setRemindLoading(true);
    try {
      await remindFundRequest(id);
      const ts = Date.now();
      localStorage.setItem(remindCooldownKey, String(ts));
      setRemindCooldown(ts);
      alert('Pengingat dikirim ke approver');
    } catch (err) {
      const msg = err?.response?.data?.message || 'Gagal mengirim pengingat';
      alert(msg);
    } finally {
      setRemindLoading(false);
    }
  };

  const handleOpenRelease = () => {
    const baseAmount = Number(data?.totalAmount || 0);
    const formattedAmount = baseAmount > 0
      ? baseAmount.toLocaleString('id-ID', { maximumFractionDigits: 0 })
      : '';
    setReleaseInfo({
      referenceNo: data?.requestNo || '',
      amount: formattedAmount,
      description: ''
    });
    setReleaseFile(null);
    setDialog('release');
  };

  const handleAction = async (action) => {
    try {
      if (action === 'approve') {
        await approveFundRequest(id, { stepId: pendingStep.id, note: actionNote });
      } else if (action === 'reject') {
        await rejectFundRequest(id, { stepId: pendingStep.id, note: actionNote });
      } else if (action === 'release') {
        const rawAmount = (releaseInfo.amount || '').replace(/[^\d]/g, '');
        const fallbackAmount = Number(data?.totalAmount || 0);
        const amountNumber = rawAmount ? Number(rawAmount) : fallbackAmount;
        if (!releaseFile) {
          alert('Upload bukti transfer wajib diisi.');
          return;
        }
        const uploadRes = await uploadFundAttachment(releaseFile);
        const attachmentUrl = uploadRes?.url || uploadRes?.file?.url || uploadRes?.fileUrl;
        await releaseFundRequest(id, {
          referenceNo: releaseInfo.referenceNo,
          amount: amountNumber,
          attachmentUrl,
          description: releaseInfo.description
        });
      }
      setDialog(null);
      setActionNote('');
      setReleaseInfo({ referenceNo: '', amount: '', description: '' });
      setReleaseFile(null);
      await fetchData();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;
  if (!data) return <Typography>Data tidak ditemukan</Typography>;

  const showRequestNumber = ['approved', 'released'].includes(effectiveStatus);
  const attachments = data.attachments || [];
  const travelDetail = data.travelDetail;
  const passengers = travelDetail?.passengers || [];
  const hasItems = data.type !== 'TRAVEL' && Array.isArray(data.items) && data.items.length > 0;
  const requestNoText = showRequestNumber ? (data.requestNo || '-') : 'MENUNGGU APPROVAL FINAL';
  const quickStats = [
    {
      label: 'Total Pengajuan',
      value: formatCurrency(data.totalAmount),
      helper: TYPE_LABELS[data.type] || data.type
    },
    {
      label: 'Status',
      value: <StatusChip status={effectiveStatus} />,
      helper: pendingStep ? `Menunggu: ${(pendingStep.role?.name || '').toUpperCase()}` : undefined
    },
    {
      label: 'Nomor Pengajuan',
      value: requestNoText,
      helper: showRequestNumber ? 'Sudah final' : 'Akan dibuat setelah semua approval selesai'
    },
    {
      label: 'Tanggal Pengajuan',
      value: formatDateTimeLabel(data.submissionDate, data.submissionTime),
      helper: `Pencairan: ${formatDateLong(data.plannedTransferDate || data.submissionDate)}`
    }
  ];

  return (
    <Container maxWidth="xl" sx={{ px: { xs: 0, md: 1 } }}>
      <Card
        variant="outlined"
        sx={{
          mb: 2,
          borderRadius: 2,
          borderColor: 'divider',
          boxShadow: '0 1px 3px rgba(16,24,40,0.08)'
        }}
      >
        <CardContent>
          <Stack direction={{ xs: 'column', lg: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', lg: 'center' }} spacing={2}>
            <Stack spacing={0.75}>
              <Typography variant="h4" sx={{ fontWeight: 800 }}>{data.title || 'DETAIL PENGAJUAN'}</Typography>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <StatusChip status={effectiveStatus} />
                <Chip size="small" label={TYPE_LABELS[data.type] || data.type} />
                <Chip size="small" variant="outlined" label={data.project?.name || 'HO / Non Project'} />
                <Chip size="small" variant="outlined" label={`NO: ${requestNoText}`} />
              </Stack>
              {!showRequestNumber && (
                <Typography variant="body2" color="text.secondary">
                  NOMOR PENGAJUAN AKAN DIBUAT SETELAH SELURUH PENYETUJU MENYETUJUI PENGAJUAN INI.
                </Typography>
              )}
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: { xs: '100%', lg: 'auto' } }}>
              <Button variant="outlined" onClick={() => navigate('/fund-requests')} sx={{ minWidth: 120 }}>KEMBALI</Button>
              {canApprove && (
                <>
                  <Button variant="contained" color="error" startIcon={<CloseIcon />} onClick={() => setDialog('reject')} sx={{ minWidth: 120 }}>TOLAK</Button>
                  <Button variant="contained" color="success" startIcon={<CheckIcon />} onClick={() => setDialog('approve')} sx={{ minWidth: 120 }}>SETUJUI</Button>
                </>
              )}
              {canRelease && (
                <Button variant="contained" color="primary" startIcon={<PaymentsIcon />} onClick={handleOpenRelease} sx={{ minWidth: 120 }}>CAIRKAN</Button>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {quickStats.map((stat) => (
          <Grid item xs={12} sm={6} md={3} key={stat.label}>
            <QuickStatCard label={stat.label} value={stat.value} helper={stat.helper} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={8}>
          <SectionCard title="INFORMASI PEMOHON">
            <InfoGrid
              items={[
                { label: 'NAMA PENGAJU', value: data.requester?.fullName || data.requester?.username || '-' },
                { label: 'EMAIL', value: data.requester?.email || '-' },
                { label: 'PROYEK', value: data.project?.name || 'HO / NON PROYEK', helper: data.project?.kodeProyek ? `KODE: ${data.project.kodeProyek}` : undefined, fullWidth: true }
              ]}
            />
          </SectionCard>

          <SectionCard title="INFORMASI PENGAJUAN">
            <InfoGrid
              items={[
                { label: 'JENIS PENGAJUAN', value: TYPE_LABELS[data.type] || data.type },
                { label: 'STATUS', value: <StatusChip status={effectiveStatus} /> },
                { label: 'TANGGAL PENGAJUAN', value: formatDateTimeLabel(data.submissionDate, data.submissionTime) },
                { label: 'TANGGAL PENCAIRAN', value: formatDateLong(data.plannedTransferDate || data.submissionDate) },
                {
                  label: 'NOMOR PENGAJUAN',
                  value: showRequestNumber ? (data.requestNo || '-') : (
                    <Typography variant="body2" color="text.secondary">
                      NOMOR PENGAJUAN AKAN DIBUAT SETELAH SELURUH PENYETUJU MENYETUJUI PENGAJUAN INI.
                    </Typography>
                  ),
                  fullWidth: true
                },
                { label: 'KATEGORI PENGELUARAN', value: data.category || '-' },
                { label: 'ALASAN / KETERANGAN TAMBAHAN', value: data.description || '-', fullWidth: true }
              ]}
            />
          </SectionCard>

          {hasItems && (
            <SectionCard title="RINCIAN ITEM PENGAJUAN">
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>NO</TableCell>
                      <TableCell>DESKRIPSI</TableCell>
                      <TableCell align="right">JUMLAH</TableCell>
                      <TableCell>UNIT</TableCell>
                      <TableCell align="right">HARGA SATUAN</TableCell>
                      <TableCell align="right">SUBTOTAL</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.items.map((item, index) => (
                      <TableRow key={item.id || index}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{item.description || '-'}</TableCell>
                        <TableCell align="right">{item.qty || '-'}</TableCell>
                        <TableCell>{item.unit || '-'}</TableCell>
                        <TableCell align="right">{formatCurrency(item.unitPrice)}</TableCell>
                        <TableCell align="right">{formatCurrency(item.lineTotal || (item.qty * item.unitPrice))}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={5} align="right" sx={{ fontWeight: 600 }}>
                        TOTAL
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {formatCurrency(data.totalAmount)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </SectionCard>
          )}

          {travelDetail && (
            <SectionCard title="DETAIL PERJALANAN">
              <InfoGrid
                items={[
                  { label: 'Tujuan Keberangkatan', value: travelDetail.purpose || '-' },
                  { label: 'Mandor / Penanggung Jawab', value: resolveMandorName(travelDetail.mandorNameManual, travelDetail.mandorId, travelDetail.mandorName) },
                  { label: 'Bandara Keberangkatan', value: travelDetail.departureCityName || '-' },
                  { label: 'Bandara Tujuan', value: travelDetail.destinationCityName || '-' },
                  { label: 'Tanggal & Jam Berangkat', value: formatDateTimeLabel(travelDetail.departureDate, travelDetail.departureTime) },
                  {
                    label: 'Tanggal & Jam Pulang',
                    value: travelDetail.returnDate || travelDetail.returnTime
                      ? formatDateTimeLabel(travelDetail.returnDate, travelDetail.returnTime)
                      : '-'
                  }
                ]}
              />
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Data Penumpang</Typography>
              {passengers.length ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Nama Lengkap</TableCell>
                        <TableCell>Nomor KTP</TableCell>
                        <TableCell>Tanggal Lahir</TableCell>
                        <TableCell>Nomor HP</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {passengers.map(passenger => (
                        <TableRow key={passenger.id}>
                          <TableCell>{passenger.name}</TableCell>
                          <TableCell>{passenger.ktpNumber || '-'}</TableCell>
                          <TableCell>{passenger.birthDate ? formatDateLong(passenger.birthDate) : '-'}</TableCell>
                          <TableCell>{passenger.phone || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary">Belum ada data penumpang.</Typography>
              )}
            </SectionCard>
          )}

          {data.cashAdvance && (
            <SectionCard title="DETAIL KASBON">
              <InfoGrid
                items={[
                  { label: 'Mandor', value: resolveMandorName(data.cashAdvance.mandorNameManual, data.cashAdvance.mandorId, data.cashAdvance.mandorName) },
                  { label: 'Kategori Kasbon', value: data.cashAdvance.cashCategory || '-' },
                  { label: 'Nominal Kasbon', value: formatCurrency(data.totalAmount) },
                  { label: 'Keterangan', value: data.description || '-', fullWidth: true }
                ]}
              />
            </SectionCard>
          )}

          <SectionCard title="LAMPIRAN">
            <Stack spacing={1.5}>
              {attachments.length ? attachments.map((att, idx) => {
                const info = normalizeAttachment(att, idx);
                if (!info) return null;
                return (
                  <Card variant="outlined" key={info.id}>
                    <CardContent>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="body2" fontWeight={600}>{info.fileName}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {(info.fileType || 'Unknown type')}{info.fileSize ? ` • ${formatFileSize(info.fileSize)}` : ''}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={1}>
                          {(info.isImage || info.isPdf) && (
                            <Button
                              size="small"
                              component="a"
                              href={info.url}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Preview
                            </Button>
                          )}
                          <Button
                            size="small"
                            component="a"
                            href={info.url}
                            target="_blank"
                            rel="noreferrer"
                            startIcon={<DownloadIcon />}
                          >
                            UNDUH
                          </Button>
                        </Stack>
                      </Stack>
                      {info.isImage && (
                        <Box
                          component="img"
                          src={info.url}
                          alt={info.fileName}
                          sx={{
                            mt: 2,
                            width: '100%',
                            maxHeight: 320,
                            objectFit: 'contain',
                            borderRadius: 1,
                            border: theme => `1px solid ${theme.palette.divider}`
                          }}
                        />
                      )}
                      {info.isPdf && (
                        <Box
                          component="iframe"
                          src={info.url}
                          title={`preview-${info.id}`}
                          sx={{
                            mt: 2,
                            width: '100%',
                            minHeight: 300,
                            borderRadius: 1,
                            border: theme => `1px solid ${theme.palette.divider}`
                          }}
                        />
                      )}
                      {!info.isImage && !info.isPdf && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                          PRATINJAU TIDAK TERSEDIA UNTUK TIPE FILE INI. KLIK UNDUH UNTUK MEMBUKA DOKUMEN.
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                );
              }) : (
                <Typography variant="body2" color="text.secondary">Tidak ada lampiran</Typography>
              )}
            </Stack>
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={4} sx={{ alignSelf: 'flex-start' }}>
          <Box sx={{ position: { md: 'sticky' }, top: { md: 16 } }}>
          <SectionCard title="RINGKASAN DANA">
            <Typography variant="body2" color="text.secondary">TOTAL PENGAJUAN</Typography>
            <Typography variant="h5" sx={{ mb: 2 }}>{formatCurrency(data.totalAmount)}</Typography>
            <InfoGrid
              items={[
                { label: 'PEMBUAT', value: data.requester?.fullName || data.requester?.username || '-' },
                { label: 'JENIS PENGAJUAN', value: TYPE_LABELS[data.type] || data.type },
                { label: 'PROJECT / HO', value: data.project?.name || 'HO / Non Project' },
                { label: 'TANGGAL PENGAJUAN', value: formatDateLong(data.submissionDate) },
                { label: 'TANGGAL PENCAIRAN', value: formatDateLong(data.plannedTransferDate || data.submissionDate) }
              ]}
            />
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>REKENING TUJUAN</Typography>
            <InfoGrid
              items={[
                { label: 'NAMA BANK', value: data.bankName || '-' },
                { label: 'NOMOR REKENING', value: data.accountNumber || '-' },
                { label: 'ATAS NAMA', value: data.accountHolder || data.payeeName || '-' }
              ]}
            />
            {data.disbursement ? (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>BUKTI TRANSFER</Typography>
                <Stack spacing={1}>
                  <InfoGrid
                    items={[
                      { label: 'REFERENCE NO', value: data.disbursement.referenceNo || '-' },
                      { label: 'JUMLAH DICAIRKAN', value: formatCurrency(data.disbursement.amount || data.totalAmount) },
                      { label: 'TANGGAL CAIR', value: formatTimestamp(data.disbursement.releasedAt) },
                      { label: 'KETERANGAN', value: data.disbursement.description || '-' }
                    ]}
                  />
                  {data.disbursement.attachmentUrl && (
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="outlined"
                        size="small"
                        component="a"
                        href={buildFileUrl(data.disbursement.attachmentUrl)}
                        target="_blank"
                        rel="noreferrer"
                        startIcon={<DownloadIcon />}
                      >
                        DOWNLOAD BUKTI
                      </Button>
                    </Stack>
                  )}
                </Stack>
              </>
            ) : null}
          </SectionCard>

          <SectionCard
            title="STATUS & JEJAK PERSETUJUAN"
            subtitle="Pengingat untuk approver aktif (cooldown 1 jam)"
            actions={(
              <Button
                size="small"
                variant="outlined"
                onClick={handleRemind}
                disabled={remindDisabled}
              >
                {remindLoading ? 'Mengirim...' : 'Kirim Pengingat'}
              </Button>
            )}
          >
            <Typography variant="caption" color="text.secondary">
              {remindHint}
            </Typography>
            <ApprovalHistory steps={data.approvalSteps} />
          </SectionCard>
          </Box>
        </Grid>
      </Grid>

      <Dialog open={!!dialog} onClose={() => setDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialog === 'approve' && 'SETUJUI PENGAJUAN'}
          {dialog === 'reject' && 'TOLAK PENGAJUAN'}
          {dialog === 'release' && 'CAIRKAN DANA'}
        </DialogTitle>
        <DialogContent>
          {dialog === 'release' ? (
            <Stack spacing={2} mt={1}>
              <TextField
                label="NOMOR REFERENSI"
                value={releaseInfo.referenceNo}
                onChange={e => setReleaseInfo(prev => ({ ...prev, referenceNo: e.target.value }))}
              />
              <TextField
                label="JUMLAH"
                type="text"
                inputMode="numeric"
                value={releaseInfo.amount}
                onChange={e => {
                  const raw = e.target.value.replace(/\./g, '').replace(/,/g, '');
                  const numeric = raw.replace(/[^\d]/g, '');
                  const formatted = numeric ? Number(numeric).toLocaleString('id-ID', { maximumFractionDigits: 0 }) : '';
                  setReleaseInfo(prev => ({ ...prev, amount: formatted }));
                }}
              />
              <TextField
                label="KETERANGAN"
                multiline
                minRows={2}
                value={releaseInfo.description}
                onChange={e => setReleaseInfo(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Contoh: nomor voucher, catatan pencairan, dsb."
              />
              <Button variant="outlined" component="label">
                {releaseFile ? `Bukti: ${releaseFile.name}` : 'Upload Bukti Transfer (wajib)'}
                <input
                  type="file"
                  hidden
                  accept=".jpg,.jpeg,.png,.pdf"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) setReleaseFile(file);
                  }}
                />
              </Button>
              <Typography variant="body2" color="text.secondary">
                Pastikan data sesuai slip transfer sebelum menekan "Cairkan".
              </Typography>
            </Stack>
          ) : (
            <TextField
              label="CATATAN (OPSIONAL)"
              fullWidth
              multiline
              minRows={2}
              value={actionNote}
              onChange={e => setActionNote(e.target.value)}
              sx={{ mt: 1 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>BATAL</Button>
          <Button variant="contained" onClick={() => handleAction(dialog)}>
            {dialog === 'approve' && 'SETUJUI'}
            {dialog === 'reject' && 'TOLAK'}
            {dialog === 'release' && 'CAIRKAN'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default FundRequestDetail;
